import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAttendanceDetails } from "../../actions/AttendanceDetailsAction";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Popover,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ClearIcon from "@mui/icons-material/Clear";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import EmployeePhoto from "./EmployeePhoto";

// ─────────────────────────────────────────────
// API response shape (confirmed):
// { serviceNo, name, location, locName, inTime, outTime, OTstatus, date }
// ─────────────────────────────────────────────

const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: "1000px",
  margin: "0 auto",
  padding: "8px 12px",
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("sm")]: { padding: "0px 8px" },
}));


const SummaryCard = styled(Box)(({ cardcolor }) => ({
  background:
    cardcolor === "present"
      ? "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
      : cardcolor === "absent"
      ? "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)"
      : "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
  border:
    cardcolor === "present"
      ? "1px solid #a5d6a7"
      : cardcolor === "absent"
      ? "1px solid #ef9a9a"
      : "1px solid #aec4e0",
  padding: "12px 16px",
  borderRadius: "12px",
  textAlign: "center",
  fontWeight: 600,
  color:
    cardcolor === "present" ? "#1b5e20" : cardcolor === "absent" ? "#b71c1c" : "#0E3C66",
  boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
}));

const EmployeeCard = styled(Box)(({ theme }) => ({
  padding: "10px 12px",
  borderRadius: "10px",
  marginBottom: "8px",
  boxShadow: "0px 3px 8px rgba(0, 0, 0, 0.07)",
  backgroundColor: "#fff",
  border: "1px solid #d1d9e6",
  transition: "0.15s ease",
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.06)",
    cursor: "pointer",
    transform: "translateY(-1px)",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.12)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 10px",
  },
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  marginTop: "8px",
  [theme.breakpoints.down("sm")]: {
    maxHeight: "calc(100vh - 260px)",
    overflowY: "auto",
  },
}));

// Helpers
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function AttendanceDetails() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { loading, data, error } = useSelector(
    (state) => state.attendanceDetails || { loading: false, data: [], error: null }
  );

  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [tempSelectedLocations, setTempSelectedLocations] = useState([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [exporting, setExporting] = useState(false);

  const openFilter = Boolean(filterAnchorEl);

  // Auto-fetch when date changes
  useEffect(() => {
    if (selectedDate) {
      dispatch(fetchAttendanceDetails(selectedDate));
    }
  }, [dispatch, selectedDate]);

  // ── Unique Locations (use locName as display label, location as filter key) ──
  const uniqueLocations = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const locs = [...new Set(data.map((item) => item.locName).filter(Boolean))];
    return locs.sort();
  }, [data]);

  // ── Filtered Data ──
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    let filtered = data;

    // Filter by selected locations (using locName)
    if (selectedLocations.length > 0) {
      filtered = filtered.filter((item) => selectedLocations.includes(item.locName));
    }

    // Search by serviceNo, name, locName, location
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.serviceNo && item.serviceNo.toString().toLowerCase().includes(term)) ||
          (item.name && item.name.toLowerCase().includes(term)) ||
          (item.locName && item.locName.toLowerCase().includes(term)) ||
          (item.location && item.location.toLowerCase().includes(term))
      );
    }
    return filtered;
  }, [data, searchTerm, selectedLocations]);

  // ── Stats: present = has inTime, absent = no inTime ──
  const totalPresent = useMemo(
    () => filteredData.filter((d) => d.inTime && d.inTime.trim() !== "").length,
    [filteredData]
  );
  const totalAbsent = useMemo(
    () => filteredData.filter((d) => !d.inTime || d.inTime.trim() === "").length,
    [filteredData]
  );

  const showSnackbar = (msg, severity = "success") =>
    setSnackbar({ open: true, message: msg, severity });

  const handleRefresh = () => {
    dispatch(fetchAttendanceDetails(selectedDate));
    showSnackbar("Data refreshed", "success");
  };

  // Location filter handlers
  const handleFilterClick = (e) => {
    setTempSelectedLocations(selectedLocations);
    setFilterAnchorEl(e.currentTarget);
  };
  const handleFilterClose = () => setFilterAnchorEl(null);
  const handleLocationToggle = (loc) =>
    setTempSelectedLocations((prev) =>
      prev.includes(loc) ? prev.filter((l) => l !== loc) : [...prev, loc]
    );
  const handleSelectAll = () =>
    setTempSelectedLocations(
      tempSelectedLocations.length === uniqueLocations.length ? [] : [...uniqueLocations]
    );
  const handleApplyFilter = () => {
    setSelectedLocations(tempSelectedLocations);
    handleFilterClose();
    if (tempSelectedLocations.length > 0)
      showSnackbar(`Filtered by ${tempSelectedLocations.length} location(s)`, "info");
  };
  const handleClearFilter = () => {
    setSelectedLocations([]);
    setTempSelectedLocations([]);
    handleFilterClose();
    showSnackbar("Filter cleared", "info");
  };

  // PDF Export
  const handleExportPDF = async () => {
    if (exporting || filteredData.length === 0) return;
    setExporting(true);
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Attendance Details Report", pageWidth / 2, 18, { align: "center" });
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Date: ${selectedDate}`, 20, 30);
      doc.text(`Total Records: ${filteredData.length}  |  Present: ${totalPresent}  |  Absent: ${totalAbsent}`, 20, 37);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 44);

      autoTable(doc, {
        head: [["#", "Svc No", "Name", "Location", "In Time", "Out Time", "OT Status"]],
        body: filteredData.map((item, idx) => [
          idx + 1,
          item.serviceNo || "N/A",
          item.name || "N/A",
          item.locName || item.location || "N/A",
          item.inTime || "—",
          item.outTime || "—",
          item.OTstatus || "—",
        ]),
        startY: 52,
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [21, 76, 121], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 248, 252] },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: "right" });
      }
      doc.save(`AttendanceDetails_${selectedDate}.pdf`);
      showSnackbar("PDF exported successfully!", "success");
    } catch (err) {
      console.error("PDF error:", err);
      showSnackbar("Failed to export PDF", "error");
    }
    setExporting(false);
  };

  // ─────────────────────────────────────────────
  return (
    <PageContainer>
      {/* Title */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#0e3c66", flex: 1 }}>
          📋 Attendance Details
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ color: "#154c79" }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Controls */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1.5,
          alignItems: "center",
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}
      >
        {/* Date Picker — auto-loads on change */}
        <TextField
          type="date"
          size="small"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          label="Select Date"
          InputLabelProps={{ shrink: true }}
          sx={{
            flex: isMobile ? "1 1 100%" : 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": { borderColor: "#154c79" },
              "&:hover fieldset": { borderColor: "#0D3A5C" },
            },
          }}
        />

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search name, service no, location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }} />,
            endAdornment: searchTerm ? (
              <IconButton size="small" onClick={() => setSearchTerm("")}>
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
          sx={{
            flex: isMobile ? "1 1 100%" : 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": { borderColor: "#c0cfe0" },
            },
          }}
        />

        {/* Location Filter */}
        <Tooltip title="Filter by Location">
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
            sx={{
              height: "40px",
              borderRadius: "8px",
              borderColor: selectedLocations.length > 0 ? "#154c79" : "#c0cfe0",
              color: selectedLocations.length > 0 ? "#154c79" : "#666",
              fontWeight: selectedLocations.length > 0 ? 700 : 400,
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            Location {selectedLocations.length > 0 ? `(${selectedLocations.length})` : ""}
          </Button>
        </Tooltip>

        {/* Export PDF */}
        <Tooltip title="Export PDF">
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            disabled={exporting || filteredData.length === 0}
            sx={{
              height: "40px",
              borderRadius: "8px",
              borderColor: "#154c79",
              color: "#154c79",
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            {exporting ? "Exporting..." : "PDF"}
          </Button>
        </Tooltip>
      </Box>

      {/* Active Location Filter Chips */}
      {selectedLocations.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          {selectedLocations.map((loc) => (
            <Chip
              key={loc}
              label={loc}
              size="small"
              onDelete={() => setSelectedLocations((prev) => prev.filter((l) => l !== loc))}
              sx={{ backgroundColor: "#154c79", color: "#fff", "& .MuiChip-deleteIcon": { color: "#fff" } }}
            />
          ))}
          <Chip
            label="Clear All"
            size="small"
            onClick={() => setSelectedLocations([])}
            sx={{ backgroundColor: "#f0f4f8", color: "#154c79" }}
          />
        </Box>
      )}

      {/* Summary Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <SummaryCard cardcolor="total">
          <Typography variant="h5" fontWeight={700}>{filteredData.length}</Typography>
          <Typography variant="caption" sx={{ fontSize: "11px" }}>Total Records</Typography>
        </SummaryCard>
        <SummaryCard cardcolor="present">
          <Typography variant="h5" fontWeight={700}>{totalPresent}</Typography>
          <Typography variant="caption" sx={{ fontSize: "11px" }}>Present</Typography>
        </SummaryCard>
        <SummaryCard cardcolor="absent">
          <Typography variant="h5" fontWeight={700}>{totalAbsent}</Typography>
          <Typography variant="caption" sx={{ fontSize: "11px" }}>Absent / No Record</Typography>
        </SummaryCard>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#154c79" }} />
          <Typography sx={{ ml: 2, color: "#154c79", fontWeight: 500 }}>
            Loading attendance data...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Paper
          sx={{
            p: 3, textAlign: "center",
            backgroundColor: "#fff3f3", border: "1px solid #ffcccc", borderRadius: "10px", mb: 2,
          }}
        >
          <Typography color="error" fontWeight={600}>⚠️ Error: {error}</Typography>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            sx={{ mt: 1, borderColor: "#154c79", color: "#154c79", textTransform: "none" }}
          >
            Try Again
          </Button>
        </Paper>
      )}

      {/* No Data */}
      {!loading && !error && filteredData.length === 0 && (
        <Paper
          sx={{
            p: 4, textAlign: "center",
            backgroundColor: "#f8fbff", border: "1px dashed #aec4e0", borderRadius: "10px",
          }}
        >
          <Typography sx={{ color: "#666", fontWeight: 500 }}>
            No attendance records found for <strong>{selectedDate}</strong>.
            <br />
            Try selecting a different date.
          </Typography>
        </Paper>
      )}

      {/* Employee Cards List */}
      {!loading && !error && filteredData.length > 0 && (
        <>
          <Typography sx={{ fontSize: "12px", color: "#888", mb: 0.5 }}>
            Showing {filteredData.length} of {Array.isArray(data) ? data.length : 0} records
            {selectedLocations.length > 0 ? ` · filtered by ${selectedLocations.length} location(s)` : ""}
          </Typography>
          <ScrollableContent>
            {filteredData.map((item, idx) => {
              const hasIn = item.inTime && item.inTime.trim() !== "";
              return (
                <EmployeeCard key={idx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "nowrap" }}>
                    {/* Employee Image */}
                    <EmployeePhoto serviceNo={item.serviceNo} name={item.name} />

                    {/* Employee Details */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: "#154c79", display: "block", fontSize: "11px" }}
                      >
                        {item.serviceNo || "N/A"}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        sx={{
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {item.name || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          display: "block",
                          fontSize: "11px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {item.locName || item.location || "N/A"}
                      </Typography>
                    </Box>

                    {/* Attendance Times & OT Status */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: isMobile ? 1 : 2,
                        flexShrink: 0,
                      }}
                    >
                      {/* In Time */}
                      <Box sx={{ textAlign: "center", minWidth: isMobile ? "45px" : "60px" }}>
                        <Typography sx={{ fontSize: "10px", color: "#888", display: "block" }}>
                          In
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: hasIn ? 700 : 400,
                            color: hasIn ? "#1b5e20" : "#bbb",
                          }}
                        >
                          {item.inTime || "—"}
                        </Typography>
                      </Box>

                      {/* Out Time */}
                      <Box sx={{ textAlign: "center", minWidth: isMobile ? "45px" : "60px" }}>
                        <Typography sx={{ fontSize: "10px", color: "#888", display: "block" }}>
                          Out
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: item.outTime ? 700 : 400,
                            color: item.outTime ? "#b71c1c" : "#bbb",
                          }}
                        >
                          {item.outTime || "—"}
                        </Typography>
                      </Box>

                      {/* OT Status */}
                      {!isMobile && item.OTstatus && (
                        <Chip
                          label={item.OTstatus}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(21,76,121,0.08)",
                            color: "#154c79",
                            fontWeight: 600,
                            fontSize: "11px",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {/* OT Status on mobile */}
                  {isMobile && item.OTstatus && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                      <Chip
                        label={`OT: ${item.OTstatus}`}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(21,76,121,0.08)",
                          color: "#154c79",
                          fontWeight: 600,
                          fontSize: "10px",
                          height: "18px",
                        }}
                      />
                    </Box>
                  )}
                </EmployeeCard>
              );
            })}
          </ScrollableContent>
        </>
      )}

      {/* Location Filter Popover */}
      <Popover
        open={openFilter}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            p: 2, minWidth: 220, maxHeight: 360, overflowY: "auto",
          },
        }}
      >
        <Typography sx={{ fontWeight: 700, color: "#154c79", mb: 1 }}>Filter by Location</Typography>
        <Divider sx={{ mb: 1 }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={tempSelectedLocations.length === uniqueLocations.length && uniqueLocations.length > 0}
              indeterminate={tempSelectedLocations.length > 0 && tempSelectedLocations.length < uniqueLocations.length}
              onChange={handleSelectAll}
              sx={{ color: "#154c79", "&.Mui-checked": { color: "#154c79" } }}
            />
          }
          label={<Typography sx={{ fontWeight: 600, fontSize: "13px" }}>Select All</Typography>}
        />
        <Divider sx={{ mb: 1 }} />
        {uniqueLocations.map((loc) => (
          <FormControlLabel
            key={loc}
            control={
              <Checkbox
                checked={tempSelectedLocations.includes(loc)}
                onChange={() => handleLocationToggle(loc)}
                sx={{ color: "#154c79", "&.Mui-checked": { color: "#154c79" } }}
                size="small"
              />
            }
            label={<Typography sx={{ fontSize: "13px" }}>{loc}</Typography>}
          />
        ))}
        <Divider sx={{ mt: 1, mb: 1 }} />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small" variant="outlined" onClick={handleClearFilter}
            sx={{ flex: 1, textTransform: "none", borderColor: "#154c79", color: "#154c79" }}
          >Clear</Button>
          <Button
            size="small" variant="contained" onClick={handleApplyFilter}
            sx={{ flex: 1, textTransform: "none", backgroundColor: "#154c79" }}
          >Apply</Button>
        </Box>
      </Popover>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
